import Icon from '../../../../../../Icon/Icon';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../../../../../containers/UseService';
import UserSelector from '../../../../../../Input/UserSelector/UserSelector';
import Input from '../Input/Input';
import InputSelect from '../InputSelect/InputSelect';
import InputRadio from '../InputsRadio/InputRadio';
import { isValidInput, isEmpty } from '../../../../../../../utils/functions';
import Hint from '../../../../../../Tools/Hint';
import SwitcherButton from '../../../../../../Buttons/CommonSwitcher';
import { RequiredTag } from '../../../../../../Input/RequiredTag';

const Basic = (props) => {
    const { translations, user, setUser, typesUsers, edit, companies, roles } =
        props;
    const { t } = useTranslation();
    const service = useService();
    const [usernameError, setUsernameError] = useState('');
    const [nameError, setNameError] = useState('');

    const validateName = (value) => {
        setNameError('');
        if (value.length < 3) {
            setNameError(t(translations + 'nameTooShort'));
            return false;
        }
        return value.length > 0 && value != null && typeof value == 'string';
    };

    const onValidateDecimal = (value) => {
        const decimalRegex = /^(0(\.\d+)?|1(\.0+)?)$/;
        return decimalRegex.test(value);
    };

    const change = (key, value) => {
        const userCopy = JSON.parse(JSON.stringify(user));
        userCopy[key] = value;
        setUser(userCopy);
    };

    const onChangeDecimalField = (field, value) => {
        if (value === '') {
            change(field, '');
            return;
        }
        if (/^\d*\.?\d*$/.test(value)) {
            const newValue = parseFloat(value);
            if (!isNaN(newValue) && newValue >= 0 && newValue <= 1) {
                change(field, value);
            } else if (value === '0.') {
                change(field, value);
            } else {
                change(field, user[field]);
            }
        } else {
            change(field, user[field]);
        }
    };

    const checkConfirmPassword = () => {
        return user.confirmPassword !== ''
            ? user.password === user.confirmPassword
            : true;
    };

    useEffect(checkConfirmPassword, [user.password]);

    const handleChangeParentAccount = () => {
        const newIsParentAccount = !user.isParentAccount;
        const userCopy = JSON.parse(JSON.stringify(user));
        userCopy.isParentAccount = newIsParentAccount;

        if (newIsParentAccount) {
            userCopy.roleSelected = null;
        } else {
            userCopy.userSelected = null;
        }

        setUser(userCopy);
    };

    const handleChangeHasCompany = () => {
        const newHasCompany = !user.hasCompany;
        const userCopy = JSON.parse(JSON.stringify(user));
        userCopy.hasCompany = newHasCompany;

        if (!newHasCompany) {
            userCopy.company = null;
        }

        setUser(userCopy);
    };

    const validateUsername = async (username) => {
        setUsernameError('');
        if (username.length < 4) {
            setUsernameError(t(translations + 'usernameTooShort'));
            return false;
        }

        if (edit && username === user.originalUsername) {
            return true;
        }

        const validUserName = await service.isValidUsername(username);
        if (!validUserName) {
            setUsernameError(t(translations + 'usernameAlreadyExists'));
        }

        return validUserName;
    };

    const validatePassword = async (password) => {
        return password?.length >= 4 ? true : false;
    };

    return (
        <div className="w-11/12 grid-cols-1 gap-x-14 sm:gap-y-4  sm:grid sm:grid-cols-2 ">
            <Input
                label={t(translations + 'username')}
                id="username"
                change
                value={user.username}
                onChange={(username) => change('username', username)}
                onValidate={validateUsername}
                placeholder={t(translations + 'usernamePlaceholder')}
                errorMessage={usernameError}
                submitted={props.submitted}>
                <RequiredTag alert={props.submitted && isEmpty(user.username)} />
                <Hint
                    dataFor="username-hint"
                    text={t(translations + 'usernameHint')}
                    className="inline-block ml-1"
                />
            </Input>
            <Input
                label={t(translations + 'email')}
                id="email"
                value={user.email}
                onChange={(email) => change('email', email)}
                onValidate={() => isValidInput(user.email, 'email')}
                placeholder={t(translations + 'emailPlaceholder')}
                errorMessage={t(translations + 'invalidEmail')}
                submitted={props.submitted}>
                <RequiredTag alert={props.submitted && isEmpty(user.email)} />
            </Input>
            <Input
                label={t(translations + 'name')}
                id="name"
                value={user.name}
                onChange={(name) => change('name', name)}
                onValidate={() => validateName(user.name)}
                placeholder={t(translations + 'namePlaceholder')}
                errorMessage={nameError}
                submitted={props.submitted}>
                <RequiredTag alert={props.submitted && isEmpty(user.name)} />
                <Hint
                    dataFor="name-hint"
                    text={t(translations + 'nameHint')}
                    className="inline-block ml-1"
                />
            </Input>
            <Input
                type="text"
                label={t(translations + 'phoneNumber')}
                id="phone-number"
                value={user.phoneNumber}
                onChange={(phoneNumber) => change('phoneNumber', phoneNumber)}
                onValidate={() => isValidInput(user.phoneNumber, 'phone')}
                placeholder={t(translations + 'phoneNumberPlaceholder')}
                errorMessage={t(translations + 'invalidPhoneNumber')}
                submitted={props.submitted}>
                <RequiredTag alert={props.submitted && isEmpty(user.phoneNumber)} />
                <Hint
                    dataFor="phone-hint"
                    text={t(translations + 'phoneHint')}
                    className="inline-block ml-1"
                />
            </Input>
            <InputSelect
                label={t(translations + 'typeTxt')}
                id="type-users"
                options={typesUsers}
                value={user.typeUser}
                onChange={(typeUser) => change('typeUser', typeUser)}
                placeholder={t(translations + 'typePlaceholder')}>
                <RequiredTag alert={props.submitted && isEmpty(user.typeUser)} />
            </InputSelect>
            <Input
                label={t(translations + 'margin')}
                id="margin"
                value={user.margin}
                onChange={(value) => onChangeDecimalField('margin', value)}
                onValidate={onValidateDecimal}
                placeholder={t(translations + 'marginPlaceholder')}
                submitted={props.submitted}>
                <RequiredTag alert={props.submitted && isEmpty(user.margin)} />
                <Hint
                    dataFor="margin-hint"
                    text={t(translations + 'margin-hint')}
                    className="inline-block ml-1"
                />
            </Input>
            <Input
                label={t(translations + 'rebate')}
                id="rebate"
                value={user.rebate}
                onChange={(value) => onChangeDecimalField('rebate', value)}
                onValidate={onValidateDecimal}
                placeholder={t(translations + 'rebate')}
                errorMessage={t(translations + 'errorRebate')}
                submitted={props.submitted}>
                <RequiredTag alert={props.submitted && isEmpty(user.rebate)} />
                <Hint
                    dataFor="rebate-hint"
                    text={t(translations + 'margin-hint')}
                    className="inline-block ml-1"
                />
            </Input>
            {edit && (
                <div className="h-10">
                    <InputRadio
                        value={user.wantChangePassword}
                        onChange={() =>
                            change(
                                'wantChangePassword',
                                !user.wantChangePassword
                            )
                        }
                        name="change-password"
                        label={t(translations + 'changePassword')}
                    />
                </div>
            )}
            {(!edit || (edit && user.wantChangePassword)) && (
                <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-2">
                    <Input
                        autoComplete="new-password"
                        type="password"
                        label={t(translations + 'password')}
                        id="password"
                        value={user.password}
                        onChange={(password) => change('password', password)}
                        onValidate={validatePassword}
                        placeholder={t(translations + 'passwordPlaceholder')}
                        errorMessage={t(translations + 'passwordTooShort')}
                        submitted={props.submitted}>
                        <RequiredTag
                            alert={props.submitted && isEmpty(user.password)}
                        />
                        <Hint
                            dataFor="password-hint"
                            text={t(translations + 'passwordHint')}
                            className="inline-block ml-1"
                        />
                    </Input>
                    <Input
                        autoComplete="new-password"
                        type="password"
                        label={t(translations + 'confirmPassword')}
                        id="confirm-password"
                        value={user.confirmPassword}
                        onChange={(confirmPassword) =>
                            change('confirmPassword', confirmPassword)
                        }
                        onValidate={checkConfirmPassword}
                        placeholder={t(
                            translations + 'confirmPasswordPlaceholder'
                        )}
                        errorMessage={t(translations + 'passwordNotMatch')}
                        submitted={props.submitted}>
                        <RequiredTag
                            alert={
                                props.submitted && isEmpty(user.confirmPassword)
                            }
                        />
                    </Input>
                </div>
            )}

            <div className="w-3/6 ml-4">
                <label className="block mb-4">
                    <span className="text-gray-700">
                        {t(translations + 'isParentAccount')}
                    </span>
                    <div className="mt-2">
                        <SwitcherButton
                            onToggle={handleChangeParentAccount}
                            status={user.isParentAccount}
                        />
                    </div>
                </label>
            </div>

            {user.isParentAccount ? (
                <div className="max-w-sm">
                    <label className="text-sm font-medium text-gray-700">
                        {t(translations + 'parentAccount')}
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                        <div className="col-span-6">
                            <UserSelector
                                value={user.userSelected}
                                onChange={(userSelected) =>
                                    change('userSelected', userSelected)
                                }
                            />
                        </div>
                        {user.userSelected && (
                            <div className="flex justify-center items-center col-span-1">
                                <Icon
                                    name="check"
                                    fixedWidth
                                    className="text-green-500"
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <InputSelect
                    label={t(translations + 'roleTxt')}
                    id="roles"
                    options={roles}
                    value={user.roleSelected}
                    onChange={(roleSelected) =>
                        change('roleSelected', roleSelected)
                    }
                    placeholder={t(translations + 'rolePlaceholder')}
                />
            )}

            <div className="w-3/6 ml-4">
                <label className="block mb-4">
                    <span className="text-gray-700">
                        {t(translations + 'hasCompany')}
                    </span>
                    <div className="mt-2">
                        <SwitcherButton
                            onToggle={handleChangeHasCompany}
                            status={user.hasCompany}
                        />
                    </div>
                </label>
            </div>

            {user.hasCompany && (
                <InputSelect
                    label={t(translations + 'company')}
                    id="companies"
                    options={companies}
                    value={user.company}
                    onChange={(company) => change('company', company)}
                    placeholder={t(translations + 'companyPlaceholder')}
                />
            )}

            <div className="w-3/6 ml-4">
                <label className="block mb-4">
                    <span className="text-gray-700">
                        {t(translations + 'showOohOption')}
                    </span>
                    <div className="mt-2">
                        <SwitcherButton
                            onToggle={() =>
                                change('showOohOption', !user.showOohOption)
                            }
                            status={user.showOohOption}
                        />
                    </div>
                </label>
            </div>

            <div className="w-3/6 ml-4">
                <label className="block mb-4">
                    <span className="text-gray-700">
                        {t(translations + 'enable-cpm-mode')}
                    </span>
                    <div className="mt-2">
                        <SwitcherButton
                            onToggle={() => change('cpm_mode', !user.cpm_mode)}
                            status={user.cpm_mode}
                        />
                    </div>
                </label>
            </div>
        </div>
    );
};

export default Basic;
